import { useState } from "react";

import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import RadioGroup from "@shared/radio-group";

import { ConfirmCheckbox } from "../components/confirm-checkbox";

import { Table } from "./styled";

export type TrackSpendFrequency = "monthly" | "daily";
export type TrackSpendGranularity = "campaign" | "platform";

export const useSpendSheetConfirmation = ({
  onConfirm,
}: {
  onConfirm: (
    frequency: TrackSpendFrequency,
    granularity: TrackSpendGranularity,
  ) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const [confirmDisadvantages, setConfirmDisadvantages] = useState(false);
  const [confirmNoApi, setConfirmNoApi] = useState(false);

  const [frequency, setFrequency] = useState<TrackSpendFrequency>("monthly");
  const [granularity, setGranularity] =
    useState<TrackSpendGranularity>("campaign");

  return {
    open: () => {
      setIsOpen(true);
    },
    Modal: (
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setConfirmDisadvantages(false);
          setConfirmNoApi(false);
          setIsOpen(false);
        }}
        titleContent={
          <div className="font-bold">
            Confirm you understand disadvantages of using a Google Sheet
          </div>
        }
        size="xl"
      >
        <Table className="w-full mb-10 mt-6">
          <thead>
            <tr>
              <th>Google Sheet</th>
            </tr>
          </thead>
          <tbody>
            {[
              "Slower and more error prone data uploads",
              "Difficult to track multiple channels simultaneously",
              "Less robust solution",
              "Less support from Northbeam for troubleshooting",
              "Unable to track at the Adset and Ad level (Campaign level only)",
            ].map((reason, idx) => (
              <tr
                key={idx}
                className="border-t border-gray-3 odd:bg-cosmic-5 even:bg-white"
              >
                <td>
                  <i className="mr-2 fa-sharp fa-regular fa-warning font-black text-sun+2" />
                  {reason}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div className="space-y-4 flex flex-col">
          <ConfirmCheckbox
            id="sheet-disadvantages"
            checked={confirmDisadvantages}
            onChange={setConfirmDisadvantages}
            showSuccess
          >
            I understand the disadvantages of using a Google Sheet
          </ConfirmCheckbox>
          <ConfirmCheckbox
            id="sheet-no-api"
            checked={confirmNoApi}
            onChange={setConfirmNoApi}
            showSuccess
          >
            <span className="leading-6">
              I understand that by using a Google Sheet, I will not be able to
              use the Spend API
            </span>
          </ConfirmCheckbox>
        </div>
        {confirmDisadvantages && confirmNoApi && (
          <>
            <div className="h-20" />
            <div className="pt-10 pb-20 border-t border-b space-y-10">
              <h1 className="text-2xl font-semibold leading-loose">
                2. Tell us how you want to track spend data in your Google Sheet
              </h1>
              <div>
                <h2 className="text-lg font-semibold leading-tight">
                  Frequency
                </h2>
                <p>This is how often you’ll make uploads</p>
                <RadioGroup
                  options={[
                    {
                      label: "Monthly",
                      value: "monthly",
                    },
                    {
                      label: "Daily",
                      value: "daily",
                    },
                  ]}
                  onChange={setFrequency}
                  value={frequency}
                />
              </div>
              <div>
                <h2 className="text-lg font-semibold leading-tight">
                  Granularity
                </h2>
                <p>
                  This determines the ‘lowest level’ in which you can track
                  spend in Northbeam. Either the platform as a whole, or broken
                  down by campaign. This only applies to platforms tracked with
                  a Google Sheet. You’ll still see Ad level data for integrated
                  platforms (Meta Ads, Google Ads, etc.).
                </p>
                <RadioGroup
                  options={[
                    {
                      label: "Campaign",
                      value: "campaign",
                    },
                    {
                      label: "Platform",
                      value: "platform",
                    },
                  ]}
                  onChange={setGranularity}
                  value={granularity}
                />
              </div>
            </div>
          </>
        )}
        <div className="flex items-center justify-end gap-2 mt-6">
          <TertiaryButton
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Exit
          </TertiaryButton>
          <PrimaryButton
            disabled={!(confirmDisadvantages && confirmNoApi)}
            onClick={() => {
              onConfirm(frequency, granularity);
              setIsOpen(false);
            }}
          >
            Continue, use a google sheet
          </PrimaryButton>
        </div>
      </Modal>
    ),
  };
};
